// ── A/R quality metrics from the certificate's own A/R rollforward ───────────
// Field-exam style measures computed from data every BBC already carries
// (ar_rollforward_json: Beginning A/R → sales, memos, collections, discounts,
// other → Total Ending A/R).
//
//   Dilution (30/60/90d) = (credits + discounts + write-offs) ÷ gross sales over the
//                       trailing 30, 60 and 90 days — a ratio of the window's
//                       TOTALS, so a big-sales period weighs more than a small
//                       one (not an average of each certificate's own ratio).
//                       30d above 90d = dilution is rising.
//   DSO (90d)         = ending A/R ÷ (gross sales ÷ days covered), trailing 90 days
//   Collections (30d) = collections over the trailing 30 days ÷ A/R at the start
//                       of that window
//
// Windows are by DATE, not by certificate count, because borrowers report on
// different cadences (daily, weekly, monthly). A certificate's rollforward
// covers the days since the certificate before it, so whole periods are added,
// newest first, until they span the window (less a 3-day grace for weekends and
// slightly-off reporting dates). A monthly reporter's 90 days is therefore its
// last three months. The very first certificate on file has no known start and
// is never counted.
//
// A window that can't be filled — too little history — reports NOTHING rather
// than a shorter window under the longer name. (Early on, 30/60/90 would all be
// the same partial number, which reads as a real, flat trend.)
//
// Lines are matched by label because each certificate carries the borrower's
// own wording. Within a certificate each category is summed as signed and the
// total taken as an absolute value — certificates sign them differently.
// Anything that matches none of the patterns is "other" and does not enter a
// metric.
//
// ONE DEFINITION, THREE PLACES. The label rules, the numerator and the windows
// are mirrored in SQL by dbo.ARRollforwardLabel, dbo.vARRollforward and
// dbo.vARDilutionWindow (sql/2026-10-13_dilution_one_definition.sql), which
// is what Insights shows. Change one, change all three, or the Portfolio page
// and Insights will disagree again.
//
// THIS FILE EXISTS TWICE: src/apps/collateral/arMetrics.js (the pages) and
// api/shared/arMetrics.js (the Portfolio API). Keep them identical.

const DAY = 86400000;
const GRACE = 3;   // days a window may fall short and still count as filled
// A Date object (what the SQL driver returns on the API side) must go through
// toISOString — String(date) is "Tue Sep 30 2026…", which never parses, and
// every certificate was silently dropped, leaving Dilution/DSO blank on the
// Portfolio page. Strings (what the pages get, via JSON) pass through as before.
const iso10 = d => d instanceof Date ? (isFinite(d) ? d.toISOString().slice(0, 10) : "") : String(d || "").slice(0, 10);
const dayNum = d => { const t = Date.parse(iso10(d) + "T00:00:00Z"); return isFinite(t) ? Math.round(t / DAY) : null; };
const num = v => { const n = parseFloat(v); return isFinite(n) ? n : 0; };

// First match wins. Each rule is a plain substring/wildcard so it maps one to
// one onto a LIKE pattern in dbo.ARRollforwardLabel (priority in brackets).
const classifyArLine = label => {
  const l = String(label || "").trim().toLowerCase();
  if (/^beginning a\/r|^total ending a\/r|beginning.*balance|ending a\/r balance/.test(l)) return null; // [100]
  if (/collect|cash receipt|receipts|payments? received/.test(l)) return "collections";                 // [90]
  if (/discount/.test(l)) return "discounts";                                                             // [80]
  if (/write.*off|bad debt/.test(l)) return "writeoffs";                                                  // [75]
  if (/credit|return|allowance/.test(l)) return "credits";                                                // [70]
  if (/debit/.test(l)) return "debits";                                                                   // [60]
  if (/sales|invoic|billing/.test(l)) return "sales";                                                     // [50]
  return "other";
};

const parseRoll = r => {
  if (Array.isArray(r.ar_rollforward)) return r.ar_rollforward;
  try { return r.ar_rollforward_json ? JSON.parse(r.ar_rollforward_json) : []; } catch (e) { return []; }
};

// Per-certificate activity, classified. Signed sum per category, then absolute
// — the same as ABS(SUM(...)) in dbo.vARDilutionWindow.
const arActivity = r => {
  const out = { sales: 0, collections: 0, credits: 0, discounts: 0, writeoffs: 0, debits: 0, other: 0, lines: 0 };
  parseRoll(r).forEach(p => {
    const k = classifyArLine(p.label);
    if (!k) return;
    out[k] += num(p.value);
    out.lines++;
  });
  ["sales", "collections", "credits", "discounts", "writeoffs", "debits", "other"].forEach(k => { out[k] = Math.abs(out[k]); });
  return out;
};

// rows: any order. Returns a Map id → metrics for every row.
const arMetricsByRow = (rows, opts = {}) => {
  const W_DIL = opts.dilutionDays || 90, W_COL = opts.collectionDays || 30;
  const asc = rows.slice()
    .filter(r => dayNum(r.asofdate) != null)
    .sort((a, b) => dayNum(a.asofdate) - dayNum(b.asofdate));
  // each period: [start day (prior as-of), end day (as-of), activity, prior ending A/R]
  const periods = asc.map((r, i) => i === 0 ? null : {
    start: dayNum(asc[i - 1].asofdate), end: dayNum(r.asofdate),
    act: arActivity(r), priorEnd: num(asc[i - 1].ar_ending_balance),
  });
  const out = new Map();
  asc.forEach((r, i) => {
    const T = dayNum(r.asofdate);
    const win = w => {
      let s = { sales: 0, collections: 0, credits: 0, discounts: 0, writeoffs: 0, from: null, fromAr: null, n: 0 };
      for (let j = i; j >= 1; j--) {
        const p = periods[j];
        s.sales += p.act.sales; s.collections += p.act.collections;
        s.credits += p.act.credits; s.discounts += p.act.discounts; s.writeoffs += p.act.writeoffs;
        s.from = p.start; s.fromAr = p.priorEnd; s.n++;
        if (T - p.start >= w - GRACE) break;            // window filled
      }
      s.days = s.from == null ? 0 : T - s.from;
      s.full = s.days >= w - GRACE;
      if (!s.full) s.n = 0;                             // not enough history: report nothing
      return s;
    };
    const d = win(W_DIL), c = win(W_COL), d60 = win(60), d30 = win(30);
    const dil = x => x.n && x.sales > 0 ? (x.credits + x.discounts + x.writeoffs) / x.sales : null;
    const endAr = num(r.ar_ending_balance);
    out.set(r.id, {
      act: periods[i] ? periods[i].act : null,
      dilution: dil(d),
      dilution60: dil(d60), dilution60Days: d60.days,
      dilution30: dil(d30), dilution30Days: d30.days,
      dso: d.n && d.sales > 0 && d.days > 0 ? endAr / (d.sales / d.days) : null,
      dilutionDays: d.days,
      collectionsPct: c.n && c.fromAr > 0 ? c.collections / c.fromAr : null,
      collectionDays: c.days,
    });
  });
  return out;
};

export { classifyArLine, arActivity, arMetricsByRow };
